import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Check, ChevronRight, Trash2 } from "lucide-react";
import { fmtMoney } from "@/lib/finance";
import { toast } from "sonner";

export const STAGES = ["REQUESTED", "VERIFICATION", "PROCESSING", "DISPATCHED", "SETTLED"];
const STAGE_LABEL = {
  REQUESTED: "Requested", VERIFICATION: "Verification", PROCESSING: "Processing",
  DISPATCHED: "Dispatched", SETTLED: "Settled",
};
const TYPE_LABEL = {
  PROFIT_ONLY: "Profit Only", CAPITAL_WITHDRAWAL: "Capital", FULL_LIQUIDATION: "Full Liquidation",
};

const Pipeline = ({ status }) => {
  const idx = STAGES.indexOf(status);
  return (
    <div className="flex items-center gap-1 mt-3">
      {STAGES.map((s, i) => (
        <div key={s} className="flex items-center flex-1">
          <div className={`h-1.5 flex-1 rounded-full ${i <= idx ? "bg-amber-500" : "bg-[#232D40]"}`} />
        </div>
      ))}
    </div>
  );
};

export const PayoutHub = ({ payouts, onCreate, onAdvance, onDelete }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ request_type: "PROFIT_ONLY", requested_amount: "", notes: "" });

  const submit = async () => {
    const amt = parseFloat(form.requested_amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    await onCreate({ request_type: form.request_type, requested_amount: amt, notes: form.notes || null });
    toast.success("Payout request submitted");
    setOpen(false);
    setForm({ request_type: "PROFIT_ONLY", requested_amount: "", notes: "" });
  };

  const next = (status) => {
    const i = STAGES.indexOf(status);
    return i < STAGES.length - 1 ? STAGES[i + 1] : null;
  };

  return (
    <div className="glass rounded-xl border border-[#232D40] overflow-hidden" data-testid="payout-hub">
      <div className="px-5 py-4 border-b border-[#232D40] flex items-center justify-between">
        <div>
          <h2 className="font-heading text-lg font-semibold">Payout Hub</h2>
          <p className="text-xs text-slate-500">Withdrawal requests & verification pipeline</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="open-payout-btn" size="sm" className="bg-cyan-500 hover:bg-cyan-400 text-[#0B0E17] font-semibold">
              <Plus className="h-4 w-4 mr-1" />Request
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="payout-modal" className="bg-[#121722] border-[#232D40] text-slate-100 sm:max-w-[480px]">
            <DialogHeader>
              <DialogTitle className="font-heading text-cyan-400">New Payout Request</DialogTitle>
              <DialogDescription className="text-sm text-slate-400">Submit a withdrawal to Spencer Firm and track it through the verification pipeline.</DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-2">
              <div className="grid gap-2">
                <Label className="text-slate-300">Request Type</Label>
                <Select value={form.request_type} onValueChange={(v) => setForm((f) => ({ ...f, request_type: v }))}>
                  <SelectTrigger data-testid="payout-type-select" className="bg-[#0B0E17] border-[#232D40]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#121722] border-[#232D40] text-slate-100">
                    <SelectItem value="PROFIT_ONLY">Profit Only</SelectItem>
                    <SelectItem value="CAPITAL_WITHDRAWAL">Capital Withdrawal</SelectItem>
                    <SelectItem value="FULL_LIQUIDATION">Full Liquidation</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <Label className="text-slate-300">Requested Amount ($)</Label>
                <Input data-testid="payout-amount-input" type="number" value={form.requested_amount}
                  onChange={(e) => setForm((f) => ({ ...f, requested_amount: e.target.value }))}
                  className="bg-[#0B0E17] border-[#232D40] font-mono-data" />
              </div>
              <div className="grid gap-2">
                <Label className="text-slate-300">Notes</Label>
                <Textarea data-testid="payout-notes-input" value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  className="bg-[#0B0E17] border-[#232D40]" />
              </div>
              <Button data-testid="payout-submit-btn" onClick={submit} className="bg-cyan-500 hover:bg-cyan-400 text-[#0B0E17] font-semibold">
                Submit Request
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="divide-y divide-[#232D40] max-h-[360px] overflow-y-auto">
        {payouts.length === 0 && (
          <div className="p-8 text-center text-slate-500 text-sm">No payout requests yet.</div>
        )}
        {payouts.map((p) => (
          <div key={p.id} data-testid={`payout-row-${p.id}`} className="px-5 py-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="font-mono-data font-semibold text-slate-100">{fmtMoney(p.requested_amount, 0)}</p>
                <p className="text-xs text-slate-500">{TYPE_LABEL[p.request_type]} · {STAGE_LABEL[p.status]}</p>
              </div>
              <div className="flex items-center gap-2">
                {next(p.status) ? (
                  <Button data-testid={`advance-payout-${p.id}`} size="sm" variant="outline"
                    onClick={() => onAdvance(p.id, next(p.status))}
                    className="border-[#232D40] bg-white/5 text-xs h-8">
                    {STAGE_LABEL[next(p.status)]} <ChevronRight className="h-3 w-3 ml-0.5" />
                  </Button>
                ) : (
                  <span className="flex items-center gap-1 text-xs text-emerald-400 font-mono-data">
                    <Check className="h-3.5 w-3.5" />Settled
                  </span>
                )}
                <button data-testid={`delete-payout-${p.id}`} onClick={() => onDelete(p.id)}
                  className="text-slate-600 hover:text-rose-400 p-1">
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
            <Pipeline status={p.status} />
          </div>
        ))}
      </div>
    </div>
  );
};
